import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiPlus, FiEdit3, FiTrash2, FiSave, FiX } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import BottomTabMenu from '../../components/UI/BottomTabMenu';
import { clientesService } from '../../services';

const ClienteNotas = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [notas, setNotas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingNota, setEditingNota] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'general'
  });

  useEffect(() => {
    loadNotas();
  }, [id]);

  const loadNotas = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await clientesService.getNotas(id);

      if (result.success) {
        // Formatear los datos para que coincidan con el formato esperado
        const notasFormateadas = result.data.map(nota => ({
          id: nota.id,
          title: nota.title,
          content: nota.content,
          type: nota.type.toLowerCase(),
          created_at: nota.created_at,
          created_by: nota.created_by?.full_name || nota.created_by?.email || 'Usuario'
        }));
        setNotas(notasFormateadas);
      } else {
        setError(result.message || 'Error al cargar las notas');
      }
    } catch (error) {
      console.error('Error cargando notas:', error);
      setError('Error de conexión al cargar las notas');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingNota) {
        // Actualizar nota existente
        const result = await clientesService.updateNota(id, editingNota.id, formData);
        if (result.success) {
          await loadNotas(); // Recargar la lista
        } else {
          setError(result.message || 'Error al actualizar la nota');
          return;
        }
      } else {
        // Crear nueva nota
        console.log("📤 Creando nota con datos:", formData);
        const result = await clientesService.createNota(id, formData);
        if (result.success) {
          await loadNotas(); // Recargar la lista
        } else {
          setError(result.message || 'Error al crear la nota');
          return;
        }
      }

      // Resetear formulario
      setFormData({ title: '', content: '', type: 'general' });
      setShowForm(false);
      setEditingNota(null);
    } catch (error) {
      console.error('Error guardando nota:', error);
      setError('Error al guardar la nota');
    }
  };

  const handleEdit = (nota) => {
    setEditingNota(nota);
    setFormData({
      title: nota.title,
      content: nota.content,
      type: nota.type
    });
    setShowForm(true);
  };

  const handleDelete = async (notaId) => {
    if (window.confirm('¿Estás seguro de eliminar esta nota?')) {
      try {
        const result = await clientesService.deleteNota(id, notaId);
        if (result.success) {
          await loadNotas(); // Recargar la lista
        } else {
          setError(result.message || 'Error al eliminar la nota');
        }
      } catch (error) {
        console.error('Error eliminando nota:', error);
        setError('Error al eliminar la nota');
      }
    }
  };

  const handleCancel = () => {
    setFormData({ title: '', content: '', type: 'general' });
    setShowForm(false);
    setEditingNota(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTipoLabel = (tipo) => {
    const tipos = {
      general: { label: 'General', color: '#6c757d' },
      seguimiento: { label: 'Seguimiento', color: '#0d6efd' },
      importante: { label: 'Importante', color: '#dc3545' },
      recordatorio: { label: 'Recordatorio', color: '#ffc107' }
    };
    return tipos[tipo] || tipos.general;
  };

  if (isLoading) {
    return <LoadingGrid count={3} message="Cargando notas..." />;
  }

  return (
    <div className="cliente-notas-page">
      {/* Header */}
      <div className="page-header-simple">
        <button onClick={() => navigate(`/system/clientes/${id}`)} className="btn btn--ghost">
          <FiArrowLeft /> Volver
        </button>
        <h1 className="page-title">NOTAS DEL CLIENTE</h1>
        <button onClick={() => setShowForm(!showForm)} className="btn btn--primary">
          <FiPlus /> Nueva Nota
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="alert alert--danger" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {/* Formulario de Nueva Nota */}
      {showForm && (
        <div className="nota-form-card" style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ marginBottom: '15px', color: '#333' }}>
            {editingNota ? 'Editar Nota' : 'Nueva Nota'}
          </h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Tipo de Nota</label>
              <select
                className="form-input"
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                required
              >
                <option value="general">General</option>
                <option value="seguimiento">Seguimiento</option>
                <option value="importante">Importante</option>
                <option value="recordatorio">Recordatorio</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Título</label>
              <input
                type="text"
                className="form-input"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Título de la nota"
                required
                maxLength={100}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Contenido</label>
              <textarea
                className="form-input"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Escribe aquí el contenido de la nota..."
                rows={5}
                required
                maxLength={1000}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={handleCancel} className="btn btn--secondary">
                <FiX /> Cancelar
              </button>
              <button type="submit" className="btn btn--primary">
                <FiSave /> {editingNota ? 'Actualizar' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de Notas */}
      <div className="notas-list">
        {notas.length === 0 ? (
          <div className="empty-state" style={{
            textAlign: 'center',
            padding: '40px',
            backgroundColor: 'white',
            borderRadius: '8px'
          }}>
            <p style={{ color: '#6c757d', fontSize: '16px' }}>
              No hay notas registradas para este cliente.
            </p>
            <button onClick={() => setShowForm(true)} className="btn btn--primary" style={{ marginTop: '15px' }}>
              <FiPlus /> Crear Primera Nota
            </button>
          </div>
        ) : (
          notas.map((nota) => {
            const tipoInfo = getTipoLabel(nota.type);
            return (
              <div key={nota.id} className="nota-card" style={{
                backgroundColor: 'white',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '15px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                borderLeft: `4px solid ${tipoInfo.color}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                      <span style={{
                        backgroundColor: tipoInfo.color,
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {tipoInfo.label}
                      </span>
                      <span style={{ color: '#6c757d', fontSize: '14px' }}>
                        {formatDate(nota.created_at)}
                      </span>
                    </div>
                    <h4 style={{ margin: '10px 0', color: '#333', fontSize: '18px' }}>
                      {nota.title}
                    </h4>
                    <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '10px' }}>
                      {nota.content}
                    </p>
                    <small style={{ color: '#999', fontSize: '13px' }}>
                      Por: {nota.created_by}
                    </small>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginLeft: '15px' }}>
                    <button
                      onClick={() => handleEdit(nota)}
                      className="btn btn--icon"
                      style={{
                        padding: '8px',
                        color: 'white',
                        backgroundColor: '#7B2D8E',
                        border: 'none',
                        borderRadius: '6px'
                      }}
                      title="Editar"
                    >
                      <FiEdit3 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(nota.id)}
                      className="btn btn--icon"
                      style={{
                        padding: '8px',
                        color: '#7B2D8E',
                        backgroundColor: 'white',
                        border: '2px solid #7B2D8E',
                        borderRadius: '6px'
                      }}
                      title="Eliminar"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Menú de navegación inferior */}
      <BottomTabMenu
        baseRoute={`/system/clientes/${id}`}
        currentTab="notas"
      />
    </div>
  );
};

export default ClienteNotas;
